import { CommonModule, DatePipe } from '@angular/common';
import { NpsTrackingService } from './nps-service/nps-tracking.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgbRatingModule } from '@ng-bootstrap/ng-bootstrap';
import { Request } from 'npx-family-happy-common';
import { catchError, debounceTime, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ToastComponent } from 'src/app/shared/components/toast/toast.component';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { NPSScore } from 'src/app/shared/models/nps-score.model';

@Component({
  selector: 'app-request-nps',
  standalone: true,
  imports: [NgbRatingModule, ReactiveFormsModule, MatExpansionModule, ReactiveFormsModule, CommonModule, DatePipe],
  templateUrl: './request-nps.component.html',
  styleUrl: './request-nps.component.scss'
})
export class RequestNpsComponent implements OnInit, OnDestroy {
  @Input() request!: Request

  readonly = false;
  toast!: ToastComponent | null;
  npsscores: NPSScore[] = [];
  save = false;
  scoreToBeEdited: NPSScore | null = null;
  private unsubscribe = new Subject<void>()

  form = new FormGroup({
    rating: new FormControl(),
    notes: new FormControl(''),
  })

  get rating() {
    return this.form.get('rating')?.value;
  }

  constructor(private npsTrackingService: NpsTrackingService, private toastService: ToastService) { }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.npsTrackingService.resetNPSScoreList();
  }

  ngOnInit(): void {
    this.toastService.getChildComponent().pipe(takeUntil(this.unsubscribe)).subscribe((component) => this.toast = component)
    this.npsTrackingService.getNPSByCustomerEmail(this.request.requestPersonalInfo.parentEmail)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((scores) => this.npsscores = scores);

    this.form.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.unsubscribe)
    ).subscribe(() => this.save = true);
  }

  saveNPS() {
    let note = this.form.get('notes')!.value;
    let obs: Observable<NPSScore>;
    if (this.scoreToBeEdited == null) {
      obs = this.npsTrackingService.createNPS(this.request.requestPersonalInfo.parentEmail, this.rating, note);
    } else {
      obs = this.npsTrackingService.updateNPS(this.scoreToBeEdited.id!, this.request.requestPersonalInfo.parentEmail, this.rating, note);
    }
    obs.pipe(
      tap(() => {
        this.toastService.setAction('Punteggio NPS aggiornato con successo!', true)
      }),
      catchError(() => {
        this.toastService.setAction("Errore durante l'aggiornamento del punteggio NPS", false)
        return of()
      }),
      takeUntil(this.unsubscribe)
    ).subscribe((score) => {
      this.scoreToBeEdited = score;
    })
  }

  getRatingColor(value: number): string {
    if (value <= 7) {
      const maxRed = 7;
      const percent = value / maxRed;
      const red = 255;
      const green = Math.round(255 * percent / 10);
      return `rgb(${red}, ${green}, 0)`;
    } else if (value <= 9) {
      // Da 7 a 8: Scala di giallo
      const minYellow = 8;
      // const percent = (value - minYellow) / 2;
      const percent = (value / minYellow) / 2
      const red = 255;
      const green = Math.round(255 * (percent));
      return `rgb(${red}, ${green}, 0)`;
    } else {
      // Da 9 a 10: Scala di verde
      const minGreen = 9;
      const percent = (value / minGreen) / 2;
      const red = 0;
      const green = Math.round(255 * (percent));
      return `rgb(${red}, ${green}, 0)`;
    }
  }

  editScore(score: NPSScore) {
    this.scoreToBeEdited = score;
    this.form.setValue({
      notes: score.notes,
      rating: score.score + 1
    }, { emitEvent: false });
    this.save = false;
  }

  resetForm() {
    this.form.reset();
    this.scoreToBeEdited = null;
  }
}
